/**
 * Link a vendor-statement invoice row to a job. Lists active projects (toggle to
 * include inactive/finished ones), filters as you type, and one tap links the
 * invoice — the backend creates the matching unbilled expense on that project.
 * Already-linked rows can be moved to another job or unlinked (expense removed).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Search, Unlink } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const isActive = (p) => (p.status || "Active") === "Active" || p.status === "In Progress";

export default function LinkInvoiceDialog({ vendor, row, statementId, onClose, onLinked }) {
  const [projects, setProjects] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [q, setQ] = useState(row.po || "");
  const [busy, setBusy] = useState(null); // project_id | "unlink"

  useEffect(() => {
    api.get("/projects").then((r) => {
      setProjects(r.data);
      // the PO column is usually the job name — pre-filter on it, but only when it actually matches something
      const po = (row.po || "").trim().toLowerCase();
      if (po && !r.data.some((p) => `${p.name} ${p.customer_name || p.customer || ""} ${p.location || ""} ${p.po || ""}`.toLowerCase().includes(po))) setQ("");
    }).catch(() => { toast.error("Could not load jobs"); setProjects([]); });
  }, [row.po]);

  const list = useMemo(() => {
    if (!projects) return [];
    const needle = q.trim().toLowerCase();
    return projects
      .filter((p) => showInactive || isActive(p) || p.project_id === row.project_id)
      .filter((p) => !needle || `${p.name} ${p.customer_name || p.customer || ""} ${p.location || ""} ${p.po || ""}`.toLowerCase().includes(needle))
      .sort((a, b) => (isActive(b) - isActive(a)) || (a.name || "").localeCompare(b.name || ""));
  }, [projects, showInactive, q, row.project_id]);

  const link = async (p) => {
    setBusy(p.project_id);
    try {
      const r = await api.post(`/master/${vendor}/invoice-links`, {
        invoice_number: row.invoice_number, project_id: p.project_id, amount: row.amount,
        trx_date: row.trx_date, po: row.po, ref: row.ref, statement_id: statementId || undefined,
      });
      toast.success(`Invoice ${row.invoice_number} linked to ${p.name} — ${fmtMoney(row.amount)} added to its unbilled expenses`);
      onLinked(r.data);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not link the invoice"); }
    finally { setBusy(null); }
  };
  const unlink = async () => {
    setBusy("unlink");
    try {
      await api.delete(`/master/${vendor}/invoice-links/${encodeURIComponent(row.invoice_number)}`);
      toast.success(`Invoice ${row.invoice_number} unlinked — its expense was removed from ${row.project_name}`);
      onLinked(null);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not unlink"); }
    finally { setBusy(null); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto bg-white text-gray-800 sm:max-w-lg" data-testid="link-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="text-gray-900">Link invoice {row.invoice_number} to a job</DialogTitle>
          <DialogDescription className="text-gray-600">
            {fmtMoney(row.amount)}{row.po ? ` · PO ${row.po}` : ""}{row.trx_date ? ` · ${row.trx_date}` : ""}. Picking a job adds this invoice to that job's unbilled expenses.
          </DialogDescription>
        </DialogHeader>

        {row.project_id && (
          <div className="flex items-center justify-between gap-2 rounded-md border border-emerald-500/40 bg-emerald-50 px-3 py-2 text-xs text-gray-800" data-testid="link-invoice-current">
            <span><span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-emerald-500 align-middle" />Linked to <strong>{row.project_name}</strong></span>
            <button type="button" onClick={unlink} disabled={busy} data-testid="link-invoice-unlink"
              className="inline-flex items-center gap-1 rounded-md border border-red-400 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-red-600 hover:bg-red-50 disabled:opacity-50">
              {busy === "unlink" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Unlink className="h-3 w-3" />} Unlink
            </button>
          </div>
        )}

        <div className="flex items-center gap-2">
          <label className="relative flex-1">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search jobs…" autoFocus data-testid="link-invoice-search"
              className="w-full rounded-md border border-gray-300 bg-white py-1.5 pl-7 pr-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#1a3fd1] focus:outline-none" style={{ colorScheme: "light", backgroundColor: "#fff", color: "#111827" }} />
          </label>
          <label className="flex shrink-0 cursor-pointer items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-gray-600">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="link-invoice-show-inactive" className="h-3.5 w-3.5 accent-[#1a3fd1]" />
            Show inactive
          </label>
        </div>

        {!projects ? (
          <p className="flex items-center gap-1.5 font-mono text-[10px] text-gray-500"><Loader2 className="h-3 w-3 animate-spin" /> Loading jobs…</p>
        ) : list.length === 0 ? (
          <p className="py-4 text-center text-xs text-gray-500" data-testid="link-invoice-empty">
            {q ? `No ${showInactive ? "" : "active "}jobs match "${q}".` : showInactive ? "No jobs yet." : "No active jobs — tick Show inactive."}
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 rounded-md border border-gray-200" data-testid="link-invoice-list">
            {list.map((p) => {
              const current = p.project_id === row.project_id;
              return (
                <li key={p.project_id}>
                  <button type="button" onClick={() => !current && link(p)} disabled={busy || current} data-testid={`link-invoice-job-${p.project_id}`}
                    className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-gray-50 disabled:cursor-default ${current ? "bg-emerald-50" : ""}`}>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-gray-900">{p.name}</span>
                      <span className="block truncate font-mono text-[10px] text-gray-500">
                        {[p.customer_name || p.customer, p.location, p.po ? `PO ${p.po}` : ""].filter(Boolean).join(" · ") || "—"}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      {!isActive(p) && <span className="rounded-full bg-gray-200 px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-wider text-gray-600">{p.status}</span>}
                      {busy === p.project_id ? <Loader2 className="h-4 w-4 animate-spin text-[#1a3fd1]" /> : current ? <Check className="h-4 w-4 text-emerald-600" /> : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
